import { utcDate } from '../../utils/format';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Alert,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  MoreVert as MoreIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { sendMessage, fetchChatSessions, clearMessages, addMessage, setCurrentSession, fetchChatHistory, deleteChatSessionThunk } from '../../store/slices/chatSlice';
import { useAuth } from '../../context/AuthContext';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { apiService } from '../../services/apiService';

const Chat: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { messages, currentSession, loading, sessions, lastDetectedBills, lastRetrievalDebug, error } = useSelector((state: RootState) => state.chat);
  const { token, authenticated, initialized, login } = useAuth();
  const location = useLocation() as any;

  const [message, setMessage] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [billContext, setBillContext] = useState<string[]>([]);
  const [selectedBills, setSelectedBills] = useState<string[]>([]);
  const [focusedBill, setFocusedBill] = useState<string | null>(null);

  const [memberContext, setMemberContext] = useState<string[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [focusedMember, setFocusedMember] = useState<string | null>(null);
  const [memberCache, setMemberCache] = useState<Record<string, any>>({});

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [billCache, setBillCache] = useState<Record<string, any>>({});
  const [billPreviewError, setBillPreviewError] = useState<string | null>(null);
  const [pdfUrls, setPdfUrls] = useState<Record<string, string>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Auto-ask support when navigating here with a predefined or typed question
  const autoSentRef = useRef<string | null>(null);
  const latestNavigationRef = useRef(location.key);
  latestNavigationRef.current = location.key;
  const [handoff, setHandoff] = useState<{ key: string; question: string } | null>(null);
  const chatAuthRequired = true;

  useEffect(() => { apiService.setToken(token || null); }, [token]);

  // Read one handoff per navigation. Auth/session changes must not replay it.
  useEffect(() => {
    let state = location.state || {};
    if (!location.state) {
      try { state = JSON.parse(sessionStorage.getItem('pending-ai-chat') || '{}') || {}; } catch {}
    }
    const bills = Array.isArray(state.billContext) ? state.billContext
      .filter((id: any) => typeof id === 'string' && /^\d+[/:][a-z]+[/:]\d+$/i.test(id))
      .map((id: string) => id.replace(/:/g, '/').toLowerCase()) : [];
    const members = Array.isArray(state.memberContext) ? state.memberContext : [];
    const q = String(state.initialQuestion || state.prefill || new URLSearchParams(location.search || '').get('q') || '').trim();
    setHandoff({ key: location.key, question: q });
    if (bills.length || members.length || q) {
      dispatch(clearMessages());
      dispatch(setCurrentSession(null));
      setBillContext(bills); setSelectedBills(bills); setFocusedBill(bills[0] || null);
      setMemberContext(members); setSelectedMembers(members); setFocusedMember(members[0] || null);
      setMessage(q);
    }
  }, [location.key, location.state, location.search, dispatch]);

  useEffect(() => {
    if (!initialized) return;
    if (authenticated) dispatch(fetchChatSessions());

  }, [dispatch, initialized, token, authenticated]);

  // Keep billContext in sync with selectedBills
  useEffect(() => {
    setBillContext(selectedBills);
  }, [selectedBills]);

  // Keep memberContext in sync with selectedMembers
  useEffect(() => {
    setMemberContext(selectedMembers);
  }, [selectedMembers]);

  // Persist context changes to the server when a session exists
  useEffect(() => {
    const sid = currentSession?.session_id;
    if (!sid) return;
    // fire-and-forget; backend will store updated lists
    (async () => {
      try {
        await apiService.updateChatSessionContext(sid, selectedBills, selectedMembers);
      } catch (e) {
        // ignore transient errors; context will also sync on next message send
      }
    })();
  }, [selectedBills, selectedMembers, currentSession]);

  // Auto-focus detected bills from backend (when user referenced a specific bill in free-form text)
  useEffect(() => {
    if (Array.isArray(lastDetectedBills) && lastDetectedBills.length > 0) {
      const first = lastDetectedBills[0];
      if (!selectedBills || selectedBills.length === 0) {
        // If no explicit selection yet, adopt detected bills wholesale
        setSelectedBills(lastDetectedBills);
        setFocusedBill(first);
      } else {
        // When the user mentions a new bill, ensure we preview it and add it to the chips if missing
        const hasFirst = selectedBills.includes(first);
        if (!hasFirst) {
          setSelectedBills(prev => [...prev, first]);
        }
        if (first && first !== focusedBill) {
          setFocusedBill(first);
        }
      }
    }
  }, [lastDetectedBills]);

  // Fallback: if no detectedBills array provided, try to focus first citation from retrievalDebug
  useEffect(() => {
    if (focusedBill) return; // already showing a bill
    if (Array.isArray(lastDetectedBills) && lastDetectedBills.length > 0) return; // primary path covers it
    const cites = (lastRetrievalDebug && Array.isArray(lastRetrievalDebug.citations)) ? lastRetrievalDebug.citations : [];
    if (!cites || cites.length === 0) return;
    try {
      const c0 = cites[0];
      const bidRaw = c0.bill || c0.id || '';
      if (!bidRaw) return;
      const bid = String(bidRaw);
      // Expect formats like "119/hr/2" or "119:hr:2"; normalize to slashes
      const parts = bid.split(/[/:]/);
      if (parts.length >= 3) {
        const norm = `${parts[0]}/${parts[1]}/${parts[2]}`;
        setFocusedBill(norm);
        setSelectedBills(prev => (prev.includes(norm) ? prev : [...prev, norm]));
      }
    } catch {}
  }, [lastRetrievalDebug, focusedBill, lastDetectedBills]);

  // Debounced search for bills
  useEffect(() => {
    const controller = new AbortController();
    const t = setTimeout(async () => {
      const q = searchTerm.trim();
      if (q.length < 2) {
        setSearchResults([]);
        return;
      }
      try {
        const resp = await apiService.getBills({ q, limit: 8, offset: 0 });
        const data = resp.data;
        const bills = Array.isArray(data?.bills) ? data.bills : [];
        setSearchResults(bills);
      } catch (e) {
        setSearchResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!focusedBill) return;
    let cancelled = false;
    setBillPreviewError(null);
    const [c, t, n] = focusedBill.split(/[/:]/);
    apiService.getBillDetail(Number(c), t, n).then(r => {
      if (!cancelled) setBillCache(prev => ({ ...prev, [focusedBill]: r.data }));
    }).catch(() => { if (!cancelled) setBillPreviewError('Bill details could not be loaded. You can still open the bill page.'); });
    return () => { cancelled = true; };
  }, [focusedBill]);

  // Fetch PDF when focused bill changes
  useEffect(() => {
    const setPdf = (billId: string) => {
      const [congress, type, number] = billId.split(/[/:]/);
      const apiBase = process.env.REACT_APP_API_BASE_URL || '/api/v1';
      const proxied = `${apiBase}/bill/${congress}/${type}/${number}/pdf`;
      setPdfUrls(prev => ({ ...prev, [billId]: proxied }));
    };
    if (focusedBill && !pdfUrls[focusedBill]) {
      setPdf(focusedBill);
    }
  }, [focusedBill, pdfUrls]);

  // Fetch focused member details when needed
  useEffect(() => {
    const loadMember = async (mid: string) => {
      try {
        const resp = await apiService.getMemberDetail(mid);
        const data = resp.data || resp;
        setMemberCache(prev => ({ ...prev, [mid]: data }));
      } catch (e) {
        // ignore
      }
    };
    if (focusedMember && !memberCache[focusedMember]) {
      loadMember(focusedMember);
    }
  }, [focusedMember, memberCache]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const submitMessage = useCallback(async (text: string) => {
    const question = text.trim();
    if (!question || loading || (chatAuthRequired && !authenticated)) return;
    dispatch(addMessage({ role: 'user', content: question, timestamp: new Date().toISOString() }));
    setMessage('');
    try {
      await dispatch(sendMessage({
        message: question,
        sessionId: currentSession?.session_id,
        billContext: selectedBills,
        memberContext: selectedMembers,
      })).unwrap();
    } catch {
      if (latestNavigationRef.current === location.key) setMessage(question); // Keep failed questions for retry without overwriting a newer handoff.
    }
  }, [dispatch, loading, chatAuthRequired, authenticated, currentSession, selectedBills, selectedMembers, location.key]);

  const handleSendMessage = () => submitMessage(message);

  useEffect(() => {
    const q = handoff && handoff.key === location.key ? handoff.question : '';
    if (!q || autoSentRef.current === location.key || !initialized || chatAuthRequired === null || loading) return;
    if (chatAuthRequired && !authenticated) return;
    // Wait until the navigation effect has reset the previous conversation.
    if (currentSession || selectedBills !== billContext) return;
    try { sessionStorage.removeItem("pending-ai-chat"); } catch {}
    autoSentRef.current = location.key;
    void submitMessage(q);
  }, [handoff, location.key, initialized, chatAuthRequired, authenticated, loading, currentSession, selectedBills, billContext, submitMessage]);

  const handleSignIn = () => {
    if (handoff?.question) {
      try { sessionStorage.setItem('pending-ai-chat', JSON.stringify({ initialQuestion: handoff.question, billContext: selectedBills, memberContext: selectedMembers })); } catch {}
    }
    login();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNewChat = () => {
    dispatch(clearMessages());
    dispatch(setCurrentSession(null));
    handleMenuClose();
  };

  const handleSelectSession = (session: any) => {
    if (!session || !session.session_id) return;
    // Set current session and load its history
    dispatch(setCurrentSession({ session_id: session.session_id }));
    dispatch(fetchChatHistory(session.session_id) as any);
    // Sync bill context UI from the session
    const ctx = Array.isArray(session.bill_context) ? session.bill_context : [];
    setSelectedBills(ctx);
    setFocusedBill(ctx[0] || null);
  };

  const handleDeleteSession = (e: React.MouseEvent, session: any) => {
    e.stopPropagation();
    if (!session || !session.session_id) return;
    dispatch(deleteChatSessionThunk(session.session_id) as any);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          AI Chat Assistant
        </Typography>
        <div>
          <IconButton onClick={handleMenuOpen}>
            <MoreIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleNewChat}>New Chat</MenuItem>
          </Menu>
        </div>
      </Box>

      {/* Auth requirement/notice */}
      {!authenticated && (
        <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Typography variant="body2">Sign in required to use AI Chat.</Typography>
          <Button size="small" sx={{ mt: 1 }} variant="contained" onClick={handleSignIn}>Sign in</Button>
        </Paper>
      )}

      {/* Context selection */}
      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Bills */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Bills in context</Typography>
            {focusedBill && billCache[focusedBill]?.title && <Typography variant="body2" sx={{ mb: 1, display: { xs: 'block', sm: 'none' } }}>{billCache[focusedBill].title}</Typography>}
            <Box display="flex" gap={1} flexWrap="wrap">
              {selectedBills.map((bid) => (
                <Chip
                  key={bid}
                  label={bid}
                  color={focusedBill === bid ? 'primary' : 'default'}
                  onClick={() => setFocusedBill(bid)}
                  onDelete={() => {
                    setSelectedBills(prev => prev.filter(b => b !== bid));
                    if (focusedBill === bid) {
                      const next = selectedBills.find(b => b !== bid) || null;
                      setFocusedBill(next);
                    }
                  }}
                />
              ))}
            </Box>
            <Box position="relative" mt={1}>
              <TextField
                fullWidth
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setShowResults(true); }}
                placeholder="Search bills to add to context (e.g., border, AI, HR 1)"
              />
              {showResults && searchResults.length > 0 && (
                <Paper elevation={3} sx={{ position: 'absolute', zIndex: 10, left: 0, right: 0 }}>
                  <List>
                    {searchResults.map((b, idx) => {
                      const id = `${b.congress}/${b.bill_type}/${b.bill_number}`;
                      const title = b.title || `${b.bill_type?.toUpperCase()} ${b.bill_number}`;
                      return (
                        <ListItemButton
                          key={id + idx}
                          onClick={() => {
                            setShowResults(false);
                            setSearchTerm('');
                            setSearchResults([]);
                            setSelectedBills(prev => prev.includes(id) ? prev : [...prev, id]);
                            setFocusedBill(id);
                          }}
                        >
                          <Box>
                            <Typography variant="body2"><strong>{b.congress}</strong> / {b.bill_type?.toUpperCase()} / {b.bill_number}</Typography>
                            <Typography variant="caption" color="text.secondary">{title}</Typography>
                          </Box>
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Paper>
              )}
            </Box>
          </Box>

          {/* Members */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Members in context</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {selectedMembers.map((mid) => (
                <Chip
                  key={mid}
                  label={mid}
                  color={focusedMember === mid ? 'secondary' : 'default'}
                  onClick={() => { setFocusedMember(mid); setFocusedBill(null); }}
                  onDelete={() => {
                    setSelectedMembers(prev => prev.filter(m => m !== mid));
                    if (focusedMember === mid) {
                      const next = selectedMembers.find(m => m !== mid) || null;
                      setFocusedMember(next);
                    }
                  }}
                />
              ))}
              {selectedMembers.length === 0 && (
                <Typography variant="caption" color="text.secondary">Select a member by starting from a Member page or via suggestions.</Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
        {/* Main Area: sessions + messages + PDF */}
        <Box sx={{ flexGrow: 1, display: 'flex', minHeight: 0 }}>
          {/* Sessions Sidebar */}
          {token && (
            <Box sx={{
              flex: '0 0 260px',
              width: 260,
              borderRight: 1,
              borderColor: 'divider',
              display: { xs: 'none', md: 'block' },
              overflow: 'auto',
            }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1}>
                <Typography variant="subtitle2">Conversations</Typography>
                <Button size="small" onClick={handleNewChat}>New</Button>
              </Box>
              <Divider />
              <List dense>
                {(sessions || []).map((s: any) => {
                  const isActive = currentSession && currentSession.session_id === s.session_id;
                  const ctx = Array.isArray(s.bill_context) ? s.bill_context : [];
                  const title = ctx[0] || 'General Chat';
                  return (
                    <ListItem key={s.session_id} disablePadding selected={Boolean(isActive)}>
                      <ListItemButton onClick={() => handleSelectSession(s)}>
                        <ListItemText
                          primaryTypographyProps={{ noWrap: true }}
                          secondaryTypographyProps={{ noWrap: true }}
                          primary={title}
                          secondary={s.session_id?.substring(0, 8) + '…'}
                        />
                        <Tooltip title="Delete conversation">
                          <IconButton edge="end" size="small" onClick={(e) => handleDeleteSession(e, s)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
                {(sessions || []).length === 0 && (
                  <ListItem>
                    <ListItemText primary="No conversations yet" secondary="Start a new chat to begin" />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          {/* Messages Area */}
          <Box sx={{ flex: '1 1 0%', minWidth: 0, overflow: 'auto', p: 2 }}>
            {messages.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                color="text.secondary"
              >
                <BotIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  Welcome to Congressional AI Assistant
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Ask me questions about congressional bills, members, or legislative processes.
                </Typography>
              </Box>
            ) : (
              <List sx={{ py: 0 }}>
                {messages.map((msg, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                      alignItems: 'flex-start',
                      py: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main',
                        mx: 1,
                      }}
                    >
                      {msg.role === 'user' ? <PersonIcon /> : <BotIcon />}
                    </Avatar>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        maxWidth: '70%',
                        bgcolor: (theme) => {
                          const isUser = msg.role === 'user';
                          if (theme.palette.mode === 'dark') {
                            return isUser ? theme.palette.primary.dark : theme.palette.grey[900];
                          }
                          return isUser ? theme.palette.primary.light : theme.palette.grey[50];
                        },
                        color: (theme) => {
                          const isUser = msg.role === 'user';
                          const bg = theme.palette.mode === 'dark'
                            ? (isUser ? theme.palette.primary.dark : theme.palette.grey[900])
                            : (isUser ? theme.palette.primary.light : theme.palette.grey[50]);
                          return theme.palette.getContrastText(bg);
                        },
                      }}
                    >
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.content}
                      </Typography>
                      {msg.timestamp && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                          {utcDate(msg.timestamp).toLocaleTimeString()}
                        </Typography>
                      )}
                    </Paper>
                  </ListItem>
                ))}
                {loading && (
                  <ListItem sx={{ display: 'flex', alignItems: 'flex-start', py: 1 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', mx: 1 }}>
                      <BotIcon />
                    </Avatar>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
                        color: (theme) => {
                          const bg = theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50];
                          return theme.palette.getContrastText(bg);
                        },
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">Assistant is typing…</Typography>
                    </Paper>
                  </ListItem>
                )}
                <div ref={messagesEndRef} />
              </List>
            )}
          </Box>

          {/* Answer details: citations & confidence */}
          {lastRetrievalDebug && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip size="small" label={`Confidence: ${typeof lastRetrievalDebug.confidence === 'number' ? Math.round(lastRetrievalDebug.confidence * 100) : Math.round((lastRetrievalDebug.confidence || 0) * 100)}%`} color={
                  (lastRetrievalDebug.confidence || 0) >= 0.7 ? 'success' : ((lastRetrievalDebug.confidence || 0) >= 0.4 ? 'warning' : 'default')
                } />
                {Array.isArray(lastRetrievalDebug.citations) && lastRetrievalDebug.citations.length > 0 && (
                  <Typography variant="caption" color="text.secondary">Citations:</Typography>
                )}
                {Array.isArray(lastRetrievalDebug.citations) && lastRetrievalDebug.citations.map((c: any, idx: number) => {
                  const bid = c.bill || c.id || '';
                  try {
                    const [congress, type, number] = String(bid).split(/[/:]/);
                    const path = `/bills/${congress}/${String(type || '').toLowerCase()}/${number}`;
                    return (
                      <Chip key={idx} size="small" label={bid} component={RouterLink as any} to={path} clickable variant="outlined" />
                    );
                  } catch {
                    return <Chip key={idx} size="small" label={String(bid)} variant="outlined" />
                  }
                })}
              </Box>
            </Box>
          )}

          {/* Side Preview: Bill PDF or Member Card */}
          {(focusedBill || focusedMember) && (
            <Divider orientation="vertical" flexItem />
          )}
          {focusedBill ? (
            <Box sx={{
              flex: '0 0 40%',
              width: '40%',
              maxWidth: '40%',
              minWidth: 280,
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              minHeight: 0,
            }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1}>
                <Typography variant="subtitle2">
                  Bill in this conversation:{' '}
                  {(() => {
                    try {
                      const [congress, type, number] = (focusedBill || '').split(/[/:]/);
                      const path = `/bills/${congress}/${(type || '').toLowerCase()}/${number}`;
                      return (
                        <Link component={RouterLink} to={path} underline="hover">
                          {focusedBill}
                        </Link>
                      );
                    } catch {
                      return <>{focusedBill}</>;
                    }
                  })()}
                </Typography>
                <IconButton size="small" onClick={() => setFocusedBill(null)}><CloseIcon fontSize="small" /></IconButton>
              </Box>
              <Divider />
              <Box sx={{ p: 2, overflow: 'auto' }}>
                {billPreviewError ? <Typography color="error">{billPreviewError}</Typography> : billCache[focusedBill]?.title ? (
                  <>
                    <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>{billCache[focusedBill].title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{billCache[focusedBill].latest_action_text || billCache[focusedBill].latest_action}</Typography>
                    <Typography variant="caption" color="text.secondary">Answers use the available bill text and legislative record.</Typography>
                  </>
                ) : <Typography role="status">Loading bill details…</Typography>}
                {pdfUrls[focusedBill] && <Button size="small" href={pdfUrls[focusedBill]} target="_blank" rel="noopener noreferrer" sx={{ mt: 1 }}>Open official PDF if available</Button>}
              </Box>
            </Box>
          ) : focusedMember ? (
            <Box sx={{
              flex: '0 0 32%',
              width: '32%',
              maxWidth: '40%',
              minWidth: 260,
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              minHeight: 0,
            }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1}>
                <Typography variant="subtitle2">
                  Member Preview:{' '}
                  <Link component={RouterLink} to={`/members/${focusedMember}`} underline="hover">
                    {focusedMember}
                  </Link>
                </Typography>
                <IconButton size="small" onClick={() => setFocusedMember(null)}><CloseIcon fontSize="small" /></IconButton>
              </Box>
              <Divider />
              <Box sx={{ flexGrow: 1, minHeight: 0, p: 2, overflow: 'auto' }}>
                {(() => {
                  const m = memberCache[focusedMember!]?.member || memberCache[focusedMember!];
                  if (!m) return <Typography variant="body2" color="text.secondary">Loading member details…</Typography>;
                  const imgUrl = m.imageUrl || m.image_url;
                  const name = m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim();
                  return (
                    <Box>
                      {imgUrl && (
                        <Box component="img" src={imgUrl} alt={name} sx={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                      )}
                      <Typography variant="h6" sx={{ mb: 0.5 }}>{name}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {[m.party, m.state, m.chamber].filter(Boolean).join(' • ')}
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                        {m.party && <Chip size="small" label={m.party} />}
                        {m.state && <Chip size="small" label={m.state} />}
                        {m.district && <Chip size="small" label={`Dist ${m.district}`} />}
                      </Box>
                      {m.summary && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.summary}</Typography>
                      )}
                      <Box mt={2}>
                        <Button component={RouterLink as any} to={`/members/${focusedMember}`} variant="outlined" size="small">
                          View full profile
                        </Button>
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          ) : null}
        </Box>

        {error && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>Unable to answer: {error}. Your question is kept below so you can retry.</Alert>}
        {initialized && !authenticated && <Alert severity="info" sx={{ mx: 2, mt: 1 }} action={<Button color="inherit" onClick={handleSignIn}>Sign in</Button>}>Sign in to ask AI{handoff?.question ? ' this question about the selected bill' : ''}.</Alert>}
        {/* Input Area */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          {(currentSession || billContext || memberContext) && (
            <Box mb={1} display="flex" gap={1} flexWrap="wrap">
              {currentSession && (
                <Chip
                  label={`Session: ${currentSession.session_id?.substring(0, 8)}...`}
                  size="small"
                  variant="outlined"
                />
              )}
              {billContext && billContext.map((bc, idx) => (
                <Chip key={`bc-${idx}`} label={`Bill Context: ${bc}`} size="small" color="primary" variant="outlined" />
              ))}
              {memberContext && memberContext.map((mc, idx) => (
                <Chip key={`mc-${idx}`} label={`Member Context: ${mc}`} size="small" color="secondary" variant="outlined" />
              ))}
            </Box>
          )}
          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about congressional bills, members, or legislative processes..."
              disabled={loading}
            />
            <Button
              variant="contained"
              aria-label="Send message"
              onClick={handleSendMessage}
              disabled={!initialized || !message.trim() || loading || !authenticated}
              sx={{ minWidth: 'auto' }}
            >
              <SendIcon />
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Chat;
